import type { FC } from 'react';
import type { VariantsOnTaskIndexProps } from '../../types';
import { observer } from 'mobx-react-lite';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import { Box, Typography } from '@mui/material';
import { InfoOutlined as InfoIcon, SubdirectoryArrowRightOutlined as SubdirectoryArrowIcon } from '@mui/icons-material';
import { ConditionVariantsContainerStyled, DeleteVariantOnTask } from '../RuleEditComponents';
import { MoreAboutVariantsDialog } from '../RuleEditVariantsOnTaskComponents';
import DependingAutocomplete from './DependingAutocomplete';
import ComparisonConditionForm from '../conditions/ComparisonConditionForm';
import SingleConditionForm from '../conditions/SingleConditionForm';
import ActionsGroup from '../actions/ActionsGroup';

const VariantsOnTask: FC<VariantsOnTaskIndexProps> = observer(({ indexTask, indexVariant }) => {
  const tasksFormStore = useCurrentTasksFormStore();

  const variantsOnTaskList = tasksFormStore.getVariantsOnTask(indexTask);

  const dependingOnConditions = variantsOnTaskList[indexVariant].__selectedDependenciesOnConditions ?? [];

  const actions = variantsOnTaskList[indexVariant].actions;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 2,
        mb: 4,
        '.visiblyDeleteButton': { visibility: 'hidden' },
        '&:hover .visiblyDeleteButton': { visibility: 'visible' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 3,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
          <Box
            sx={(t) => ({
              flex: 1,
              maxHeight: 36,
              minHeight: 36,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
              width: '100%',
              backgroundColor: 'rgba(41, 121, 255, 0.04)',
              borderRadius: t.borderRadius.md,
            })}
          >
            <Typography
              variant="subtitle2"
              sx={(t) => ({
                display: 'inline-flex',
                whiteSpace: 'nowrap',
                color: t.palette.primary.main,
                gap: 0.5,
              })}
            >
              <SubdirectoryArrowIcon />
              Variant {indexVariant + 1}
            </Typography>
            <DeleteVariantOnTask indexTask={indexTask} indexVariant={indexVariant} />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', gap: 1 }}>
            <DependingAutocomplete indexTask={indexTask} indexVariant={indexVariant} />

            {!dependingOnConditions?.length && (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <Box>
                  <InfoIcon sx={(t) => ({ fontSize: 19, color: t.palette.grey[600] })} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  <Typography variant="caption" component={'span'}>
                    Select conditions to set up variant.
                  </Typography>
                  <Box sx={{ mt: -0.25 }}>
                    <MoreAboutVariantsDialog />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {dependingOnConditions?.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
          <Box sx={{ display: 'flex', flex: 1, gap: 1, flexDirection: 'column' }}>
            <ConditionVariantsContainerStyled>
              {dependingOnConditions.map((indexCondition) => {
                const condition = variantsOnTaskList[indexVariant].condition.conditions[indexCondition];
                const isComparisonCondition =
                  Object.hasOwn(condition, 'metric') && Object.hasOwn(condition, 'second_metric');
                return isComparisonCondition ? (
                  <ComparisonConditionForm
                    key={`comparison-condition-${condition.metric}-${condition.second_metric}-${condition.operator}-${condition.type}-${indexCondition}`}
                    indexCondition={indexCondition}
                    indexTask={indexTask}
                    indexVariant={indexVariant}
                  />
                ) : (
                  <SingleConditionForm
                    key={`single-condition-${condition.metric}-${condition.operator}-${condition.type}-${indexCondition}`}
                    indexCondition={indexCondition}
                    indexTask={indexTask}
                    indexVariant={indexVariant}
                  />
                );
              })}
            </ConditionVariantsContainerStyled>
          </Box>

          <ActionsGroup actions={actions} indexTask={indexTask} indexVariant={indexVariant} />
        </Box>
      ) : null}
    </Box>
  );
});

export default VariantsOnTask;
